import { useContext, useEffect, useRef } from 'react';

import { expVal } from '@confluence/feature-experiments';
import { getSingleParam } from '@confluence/route-manager-utils/entry-points/getSingleParam.ts';
import { RoutesContext } from '@confluence/route-manager/entry-points/RoutesContext.ts';
import { useRouteActions } from '@confluence/route-manager/entry-points/RouteState.ts';
import type { FullPageEditorPresetAPI } from '@confluence/editor-presets/entry-points/useFullPageEditorPreset.ts';

import { useSnippetsState } from '../../store/snippetsStore.ts';
import { useInsertSnippet } from '../panel/useInsertSnippet.ts';

export const useInsertSnippetOnLoad = (
	editorPresetAPI?: FullPageEditorPresetAPI | null,
	collabEditInitialized?: boolean,
) => {
	const insertSnippet = useInsertSnippet(editorPresetAPI);
	const { setQueryParams } = useRouteActions();
	const { getQueryParams } = useContext(RoutesContext);
	const hasInsertedRef = useRef(false);
	const { snippets, isLoaded } = useSnippetsState();

	useEffect(() => {
		if (!expVal('cc_snippets', 'isEnabled', false)) {
			return;
		}

		const insertSnippetId = getSingleParam(getQueryParams(), 'insertSnippetId');
		if (
			!insertSnippetId ||
			!editorPresetAPI ||
			!collabEditInitialized ||
			!isLoaded ||
			hasInsertedRef.current
		) {
			return;
		}

		const snippet = snippets.find((s) => s.snippetId === insertSnippetId);
		if (!snippet) {
			return;
		}

		const success = insertSnippet(snippet);
		if (success) {
			hasInsertedRef.current = true;
			setQueryParams({ insertSnippetId: null }, /* replace= */ true);
		}
	}, [
		editorPresetAPI,
		collabEditInitialized,
		isLoaded,
		snippets,
		getQueryParams,
		setQueryParams,
		insertSnippet,
	]);
};
